import * as vp from 'vopay';
import { Badge, Box, Title, Card, Pagination, usePagination } from 'vopay';
import { Button, TableFiltersContainer, Table } from 'UI';

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { usePartnerAccounts } from '@/providers/PartnerAccounts.provider';
import { useInvoiceDetails } from '@/providers/InvoiceDetails.provider';
import { useNavMenu } from '@/providers/NavMenu.provider';
import { useCache } from '@/providers/Cache.provider';
import { usePartnerData } from '@/providers/PartnerData.provider';

import useFileDownload from '@/hooks/useFiledDownload';
import InvoicingForm from './InvoicingForm';

import type { PartnerInvoiceDetailsRequestParams } from '@/hooks/useApi/useFetchPartnerInvoiceDetails';
import { useTranslation } from 'react-i18next';

type AccountDetailsTableProps = {
  accountName: string;
  availability: boolean;
  accountId: string;
};

const InvoicingList = (): React.JSX.Element => {
  const { getModuleTitle } = useNavMenu();
  const { cache } = useCache();
  const [getInvoicingFilterSettings] = cache<{ year: string; month: string }>('invoicing-form.filter');
  const { downloadJson } = useFileDownload();
  const { response, refreshPartnerAccounts } = usePartnerAccounts();
  const { response: partnerResponse } = usePartnerData();
  const { invoiceDetails, invoicePeriod, refreshInvoiceDetails } = useInvoiceDetails();
  const [filtersApplied, setFiltersApplied] = useState(false);
  const { t } = useTranslation('translations', { keyPrefix: 'Reports.List' });
  const PAGE_SIZE = 20;

  const { pageCount, pageIndex, setPageIndex, offset } = usePagination(
    response?.PaginationMetaData?.TotalRecords || 0,
    {
      pageSize: PAGE_SIZE,
    },
  );

  const initValues = { year: getYear(), month: getPreviousMonth() };
  const parsed = parseInvoicePeriod();

  useEffect(() => {
    refreshInvoiceDetails(initValues, { useCache: false });
  }, []);

  useEffect(() => {
    refreshPartnerAccounts({
      Offset: offset,
      Limit: PAGE_SIZE,
    });
  }, [offset]);

  const tableProps: vp.TableProps<AccountDetailsTableProps> = {
    id: 'billing-package-list',
    version: '2',
    zebra: true,
    columns: [
      {
        id: 'accountName',
        header: t('accountName'),
        align: 'left',
        template: ({ data }) => data.accountName || '',
      },
      {
        id: 'availability',
        header: t('availability'),
        align: 'left',
        template: ({ data }) =>
          hasInvoiceDetails(data.accountId) ? (
            <Badge label={t('availableBadge')} color="positive" />
          ) : (
            t('notAvailableBadge')
          ),
      },
      {
        id: 'view',
        header: '',
        align: 'right',
        template: ({ data }) =>
          hasInvoiceDetails(data.accountId) ? (
            <Link to={{ pathname: `reports/${data.accountId}` }}>
              <Button size="small">{t('view')}</Button>
            </Link>
          ) : (
            ''
          ),
      },
    ],
    rows: getAccountDetails().map((account) => ({
      id: account.accountId,
      data: {
        accountName: account.accountName,
        availability: hasInvoiceDetails(account.accountId),
        accountId: account.accountId,
      },
    })),
  };

  const search = (values: PartnerInvoiceDetailsRequestParams) => {
    refreshInvoiceDetails({ year: values.year, month: values.month });
    setFiltersApplied(values.month !== getPreviousMonth() || values.year !== getYear());
  };

  return (
    <div>
      <Box mb="md" jc="sb" ai="center">
        <Title title={getModuleTitle()} />
        <Button
          id="invoicing-details-json-download"
          iconRight="download"
          disabled={partnerResponse.isRoleViewOnly}
          onClick={() =>
            downloadJson({ invoicePeriod, invoiceDetails }, `invoice-details-${parsed.month}-${parsed.year}`)
          }
        >
          {t('downloadJSONButton')}
        </Button>
      </Box>
      <Card
        style={{
          padding: '16px',
        }}
      >
        <TableFiltersContainer filtersApplied={filtersApplied}>
          <TableFiltersContainer.Trigger />
          <TableFiltersContainer.Content>
            <InvoicingForm initValues={initValues} search={search} />
          </TableFiltersContainer.Content>
        </TableFiltersContainer>
        <Table {...tableProps} />
        <Box flx="row" jc="center" mt="md" base="8">
          {pageCount > 1 && (
            <Pagination
              pageCount={pageCount}
              forcePageNumber={pageIndex}
              handlePageClick={(e) => {
                setPageIndex(e.selected);
              }}
            />
          )}
        </Box>
      </Card>
    </div>
  );

  function getAccountDetails() {
    return Object.values(response?.accountDetails || {});
  }

  function hasInvoiceDetails(accountId: string) {
    const details = invoiceDetails || {};
    const account = details[accountId] || {};

    return Object.entries(account).some(([, objectValue]) => Object.keys(objectValue).length > 0);
  }

  function getYear() {
    const cachedValue = getInvoicingFilterSettings();
    if (cachedValue) return cachedValue.year;

    const date = new Date();
    const currentYear = date.getFullYear();
    const month = date.getMonth();

    return String(month === 0 ? currentYear - 1 : currentYear);
  }

  function getPreviousMonth() {
    const cachedValue = getInvoicingFilterSettings();
    if (cachedValue) return cachedValue.month;

    const prevMonth = new Date().getMonth();
    return String(prevMonth === 0 ? 12 : prevMonth);
  }

  function parseInvoicePeriod() {
    const fromDate = invoicePeriod?.FromDate || '';
    const [year, month] = fromDate.split('-');

    return { year: year || '', month: month || '' };
  }
};

export default InvoicingList;
